import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'

/**
 * 정책 문서 2종(Privacy Policy · Store Policy) 공용 스타일 — Monolith(2안).
 *
 * 본문은 `pages/home/policies/*.md` 를 markdown-to-jsx 로 그대로 그린다. 문서에 손대지 않고
 * 타이포만 규격에 맞춘다 — md 의 `# Privacy Policy` 가 곧 페이지 제목(Thin)이라 헤더를 따로
 * 얹지 않는다(제목을 두 번 두지 않는다).
 *
 * 함정 두 가지:
 *  - `monoShared` 가 `h1, h2, h3, p` 의 margin 을 0 으로 박는다(DESIGN_SYSTEM §10 "같은 키를
 *    다시 쓰면 통째로 덮인다"). 문서 안에서는 리듬이 필요해 `.mnDoc` 안에서 다시 세운다.
 *  - md 제목이 `# **Privacy Policy**` 라 h1 안에 strong 이 들어온다. 700 이 그대로 먹으면
 *    Thin 제목이 깨져서 heading 안의 strong 은 weight 를 상속시킨다.
 */
export const policyDocStyle = {
	...monoShared,
	position: 'relative' as const,
	width: '100%',

	'.mnDoc': {
		width: '100%',
		maxWidth: 760,
		margin: '0 auto',
		padding: 'clamp(56px, 7vw, 100px) 0 clamp(64px, 8.3vw, 120px)',
		textAlign: 'left' as const,
		wordBreak: 'break-word' as const,

		h1: {
			fontSize: 'clamp(44px, 6vw, 88px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
			margin: '0 0 12px',
		},

		h2: {
			fontSize: 'clamp(26px, 2.4vw, 34px)',
			lineHeight: 1.25,
			fontWeight: 200,
			color: mono.ink,
			margin: '64px 0 20px',
		},

		h3: {
			fontSize: 18,
			lineHeight: 1.4,
			fontWeight: 500,
			color: mono.ink,
			margin: '36px 0 14px',
		},

		h4: {
			fontSize: 13,
			letterSpacing: '.14em',
			textTransform: 'uppercase' as const,
			fontWeight: 500,
			color: mono.faint,
			margin: '28px 0 12px',
		},

		'h1 strong, h2 strong, h3 strong, h4 strong': {
			fontWeight: 'inherit',
		},

		p: {
			fontSize: 16,
			lineHeight: 1.75,
			fontWeight: 300,
			color: mono.dim,
			margin: '0 0 18px',
		},

		strong: {
			fontWeight: 500,
			color: mono.ink,
		},

		'ul, ol': {
			margin: '0 0 18px',
			paddingLeft: 22,
		},

		li: {
			fontSize: 16,
			lineHeight: 1.75,
			fontWeight: 300,
			color: mono.dim,
			margin: '0 0 8px',
		},

		a: {
			color: mono.ink,
			borderBottom: `1px solid ${mono.lineSoft}`,
			textDecoration: 'none',

			'&:hover': {
				borderBottomColor: mono.ink,
			},
		},

		hr: {
			height: 0,
			margin: '40px 0',
			border: 0,
			borderTop: `1px solid ${mono.line}`,
		},

		table: {
			width: '100%',
			borderCollapse: 'collapse' as const,
			margin: '0 0 24px',
		},

		'th, td': {
			padding: '13px 0',
			borderBottom: `1px solid ${mono.line}`,
			textAlign: 'left' as const,
			fontSize: 15,
			fontWeight: 300,
			color: mono.dim,
		},

		th: {
			fontSize: 13,
			letterSpacing: '.14em',
			textTransform: 'uppercase' as const,
			fontWeight: 500,
			color: mono.faint,
		},

		blockquote: {
			margin: '0 0 18px',
			padding: '0 0 0 22px',
			borderLeft: `1px solid ${mono.line}`,
		},

		/** 조용한 상태 한 줄 — 문서를 아직 못 읽었을 때 */
		'.mnDocQuiet': {
			fontSize: 12,
			letterSpacing: '.2em',
			textTransform: 'uppercase' as const,
			fontWeight: 500,
			color: mono.faint,
		},
	},
}
